import { hasWeekStarted } from '../utils/weekClassifier';
import { planPatientCount } from '../utils/ganttUtils';
import type {
  InterventionGroupDto,
  TreatmentPlanDto,
  WeekDateRangeResponse,
  YearPlanResponse
} from '../api/generated';

// Money derived from a year plan, split the way the overview card reads it.
//
// The split is by whether the week has started, which until REH-358 was also
// the point at which a week's plan froze. It no longer is: the backend now
// accepts edits and calculations for a started week. The two figures below
// still measure something real — money whose week is already running against
// money whose week has not begun — but the word "locked" now overstates it,
// and CONTEXT.md's Locked week definition is stale on main for the same
// reason. Whether the overview keeps this boundary, and what it should be
// called, is a domain question for the ticket rather than a merge decision.
export interface YearPlanMoney {
  // Revenue planned into weeks that have already started.
  readonly lockedPotential: number;
  // Revenue in weeks not yet started: the part still wholly ahead of the
  // planner.
  readonly openPotential: number;
  // What has actually been earned: completed intervention groups at their
  // effective amount, plus completion payments already claimed.
  readonly realised: number;
  // The slice of potential that assumes a completion payment is claimed, which
  // is a human judgement no plan can promise.
  readonly completionDependent: number;
}

const EMPTY_MONEY: YearPlanMoney = {
  lockedPotential: 0,
  openPotential: 0,
  realised: 0,
  completionDependent: 0
};

// Revenue if every seat in the group is filled and completed.
function groupPotential(group: InterventionGroupDto): number {
  return (group.capacity ?? 0) * (group.reimbursementAmount ?? 0);
}

// Revenue already earned in the group: only completed seats count, each at its
// override where one was set. Shadow seats are never billable.
function groupRealised(group: InterventionGroupDto): number {
  return (group.assignedPatients ?? [])
    .filter((patient) => !patient.shadow && patient.completed)
    .reduce(
      (sum, patient) =>
        sum + (patient.reimbursementOverride ?? group.reimbursementAmount ?? 0),
      0
    );
}

function buildStartedByWeekNumber(weeks: WeekDateRangeResponse[], today?: Date): Map<number, boolean> {
  const started = new Map<number, boolean>();
  for (const week of weeks) {
    started.set(week.weekNumber, hasWeekStarted(week, today));
  }
  return started;
}

export function getPlanMoney(
  plan: TreatmentPlanDto,
  startedByWeekNumber: Map<number, boolean>
): YearPlanMoney {
  let lockedPotential = 0;
  let openPotential = 0;
  let realised = 0;

  let lastWeekNumber = -Infinity;
  for (const week of plan.weeks) {
    const weekNumber = week.weekNumber ?? 0;
    lastWeekNumber = Math.max(lastWeekNumber, weekNumber);
    // A week the year plan does not list cannot have started.
    const isStarted = startedByWeekNumber.get(weekNumber) ?? false;
    for (const group of week.groups ?? []) {
      const potential = groupPotential(group);
      if (isStarted) {
        lockedPotential += potential;
        // Only inside the started branch: a completion cannot happen in a week
        // that has not begun, and counting one there would break the invariant
        // that realised sits within locked potential.
        realised += groupRealised(group);
      } else {
        openPotential += potential;
      }
    }
  }

  // The completion payment falls due at the end of the course, so it belongs to
  // the plan's last week rather than being spread across it.
  const completionAmount = plan.completionReimbursementAmount ?? 0;
  // planPatientCount is the narrowest group in the plan, which is the right
  // ceiling here: the completion payment is paid for finishing, and a patient who
  // cannot get through every group never earns one. Counting them would overstate
  // potential, the one direction that costs money.
  const completionDependent = planPatientCount(plan) * completionAmount;
  if (completionDependent > 0) {
    const isLastWeekStarted = startedByWeekNumber.get(lastWeekNumber) ?? false;
    if (isLastWeekStarted) lockedPotential += completionDependent;
    else openPotential += completionDependent;
  }
  realised += (plan.completionReimbursementClaimedPatientIds ?? []).length * completionAmount;

  return { lockedPotential, openPotential, realised, completionDependent };
}

/**
 * Money across every treatment plan in a year plan.
 *
 * Derived from whatever year plan is passed in, which is the point: the year plan
 * page holds local, unaccepted edits, and the server cannot price those. The
 * authoritative figures still come from the backend; this is the live reading
 * while planning, the same way changeSummary already derives its patient totals
 * locally.
 */
export function getYearPlanMoney(yearPlan: YearPlanResponse | null, today?: Date): YearPlanMoney {
  if (!yearPlan) return EMPTY_MONEY;
  const startedByWeekNumber = buildStartedByWeekNumber(yearPlan.weeks, today);

  return yearPlan.treatmentPlans.reduce<YearPlanMoney>((total, plan) => {
    const money = getPlanMoney(plan, startedByWeekNumber);
    return {
      lockedPotential: total.lockedPotential + money.lockedPotential,
      openPotential: total.openPotential + money.openPotential,
      realised: total.realised + money.realised,
      completionDependent: total.completionDependent + money.completionDependent
    };
  }, EMPTY_MONEY);
}

export function getYearPlanPotential(money: YearPlanMoney): number {
  return money.lockedPotential + money.openPotential;
}
